import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import './Help.css';

interface FAQItem {
  question: string;
  answer: string | React.ReactNode;
}

const Help: React.FC = () => {
  const navigate = useNavigate();
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs: FAQItem[] = [
    {
      question: "What are tokens and how do they work?",
      answer: (
        <>
          <p>Tokens are credits used to generate AI content in Tiny Little Fox:</p>
          <ul>
            <li><strong>Character Generation:</strong> Creates unique AI-generated character images</li>
            <li><strong>Scene Generation:</strong> Generates background images and scenes</li>
            <li><strong>Story Suggestions:</strong> AI-powered text and plot recommendations</li>
          </ul>
          <p>Each AI generation uses a certain number of tokens. You can purchase more tokens anytime from your dashboard.</p>
          <p><strong>Note:</strong> Purchasing any token package automatically grants you full API access to all AI features.</p>
        </>
      )
    },
    {
      question: "How do I get started creating my first storybook?",
      answer: (
        <>
          <p>Getting started is easy:</p>
          <ol>
            <li><strong>Sign Up:</strong> Create a free account</li>
            <li><strong>Create Storybook:</strong> Click "New Storybook" from your dashboard</li>
            <li><strong>Plan Your Story:</strong> Use the AI Plot Planner to outline your narrative</li>
            <li><strong>Generate Characters:</strong> Create characters with AI-powered illustrations</li>
            <li><strong>Design Pages:</strong> Drag and drop text and images onto your pages</li>
            <li><strong>Export:</strong> Download your completed storybook</li>
          </ol>
        </>
      )
    },
    {
      question: "How do I publish my book on Amazon?",
      answer: (
        <>
          <p>Publishing on Amazon Kindle Direct Publishing (KDP) is straightforward:</p>
          <ol>
            <li><strong>Export Your Book:</strong> Download your completed storybook from Tiny Little Fox</li>
            <li><strong>Create KDP Account:</strong> Sign up at <a href="https://kdp.amazon.com" target="_blank" rel="noopener noreferrer">kdp.amazon.com</a></li>
            <li><strong>Upload Your Book:</strong> Follow KDP's upload process for your manuscript</li>
            <li><strong>Set Pricing:</strong> Choose your book's price and royalty option</li>
            <li><strong>Publish:</strong> Submit for review (usually takes 24-72 hours)</li>
          </ol>
          <p>Benefits of Amazon KDP:</p>
          <ul>
            <li>No upfront costs or minimum order requirements</li>
            <li>Print-on-demand - books are printed as orders come in</li>
            <li>Earn up to 70% royalties on sales</li>
            <li>Reach millions of Amazon customers worldwide</li>
            <li>You maintain all rights to your book</li>
          </ul>
        </>
      )
    },
    {
      question: "Do I need drawing skills to create illustrations?",
      answer: "No! That's the beauty of Tiny Little Fox. Our AI-powered tools generate professional-quality illustrations for you. Simply describe what you want, and the AI creates the artwork. You don't need any artistic or drawing skills whatsoever."
    },
    {
      question: "Can I create characters that look consistent across all pages?",
      answer: "Yes! When you generate a character, Tiny Little Fox saves it to your character gallery. You can then use that same character on multiple pages, and the AI will maintain visual consistency throughout your storybook."
    },
    {
      question: "What file formats can I export my book in?",
      answer: "You can export your storybook in formats compatible with Amazon KDP and other publishing platforms. The export includes high-resolution images suitable for professional printing."
    },
    {
      question: "How much does it cost to create a storybook?",
      answer: (
        <>
          <p><strong>Typical Cost: $15-25 for a complete children's storybook</strong></p>
          <p>The actual cost varies depending on your book's length and complexity, but on average, creating a complete children's storybook costs around <strong>$20</strong>. This includes:</p>
          <ul>
            <li>10-15 unique AI-generated character illustrations</li>
            <li>10-20 scene/background images</li>
            <li>AI-assisted plot planning and text suggestions</li>
            <li>Unlimited editing and page arrangement</li>
          </ul>
          <p><strong>Compare to Traditional Publishing Costs:</strong></p>
          <ul>
            <li><strong>Professional Illustrator:</strong> $2,000-$10,000+ for a complete book</li>
            <li><strong>Freelance Artist:</strong> $100-$300 per illustration (10-20 illustrations = $1,000-$6,000)</li>
            <li><strong>Writer/Editor:</strong> $500-$5,000 for manuscript development</li>
            <li><strong>Book Designer:</strong> $500-$2,000 for layout and formatting</li>
            <li><strong>Total Traditional Cost:</strong> $3,000-$17,000+</li>
          </ul>
          <p><strong>Tiny Little Fox saves you 95-99% compared to traditional book creation!</strong></p>
          <p>Our token-based system means:</p>
          <ul>
            <li><strong>Free Account:</strong> Sign up for free and get starter tokens to try it out</li>
            <li><strong>Purchase Tokens:</strong> Buy additional tokens as needed ($1, $5, $20, or $50 packages)</li>
            <li><strong>No Subscription:</strong> Pay only for what you use, no monthly fees</li>
            <li><strong>No Hidden Costs:</strong> Publishing on Amazon KDP is also free with print-on-demand</li>
            <li><strong>100% Your Copyright:</strong> You own complete rights to all your creations and can publish, sell, or license them freely</li>
          </ul>
        </>
      )
    },
    {
      question: "Can children use this platform?",
      answer: "Absolutely! Tiny Little Fox is perfect for children to bring their creative stories to life. We recommend parental supervision, especially for younger children. Parents can help guide the story creation process and teach children about storytelling, creativity, and even self-publishing."
    },
    {
      question: "How does the AI Plot Planner work?",
      answer: "The AI Plot Planner helps you structure your story by suggesting plot points, character arcs, and narrative flow. Simply provide a brief description of your story idea, and the AI will generate a detailed outline with suggestions for each page. You can accept, modify, or regenerate any suggestions."
    },
    {
      question: "What kind of books can I create?",
      answer: (
        <>
          <p>Tiny Little Fox supports various types of storybooks:</p>
          <ul>
            <li>Children's picture books</li>
            <li>Educational stories</li>
            <li>Fairy tales and fantasy</li>
            <li>Adventure stories</li>
            <li>Personalized books (birthdays, special occasions)</li>
            <li>Early reader books</li>
          </ul>
        </>
      )
    },
    {
      question: "Can I collaborate with others on a storybook?",
      answer: "Currently, each storybook is tied to one account. However, you can export and share your work with others for feedback. Collaborative features may be added in future updates."
    },
    {
      question: "How long does it take to create a storybook?",
      answer: "It depends on the length and complexity of your book. A simple 10-page children's book can be created in as little as 30-60 minutes using AI assistance. More complex projects with custom editing may take several hours or days, depending on your vision."
    },
    {
      question: "What if I'm not satisfied with an AI-generated image?",
      answer: "You can regenerate any AI image as many times as needed (each generation uses tokens). You can also edit your character or scene descriptions to get different results. Our AI tools are designed to give you creative control over the final output."
    },
    {
      question: "Can I use my own illustrations instead of AI-generated ones?",
      answer: "Yes! You can upload your own images to use in your storybook. Simply use the upload feature in the editor to add your custom artwork alongside or instead of AI-generated images."
    },
    {
      question: "Is there a limit to how many pages my book can have?",
      answer: "There's no strict limit on the number of pages. However, for optimal Amazon KDP publishing, we recommend keeping children's picture books between 24-40 pages. You can create longer books if your story requires it."
    },
    {
      question: "Do you offer refunds on token purchases?",
      answer: "Due to the nature of AI generation services, tokens are generally non-refundable once used. However, if you experience technical issues or have concerns, please contact our support team, and we'll work with you to find a solution."
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="help-page">
      <div className="help-header">
        <h1>Help & Support</h1>
        <p>Find answers to common questions and learn how to make the most of Tiny Little Fox</p>
      </div>

      <div className="help-content">
        <section className="faq-section">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div key={faq.question || index} className={`faq-item ${openIndex === index ? 'open' : ''}`}>
                <button className="faq-question" onClick={() => toggleFAQ(index)}>
                  <span>{faq.question}</span>
                  <span className="faq-icon">{openIndex === index ? '−' : '+'}</span>
                </button>
                {openIndex === index && (
                  <div className="faq-answer">
                    {typeof faq.answer === 'string' ? <p>{faq.answer}</p> : faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        </section>

        <section className="quick-links">
          <h2>Quick Links</h2>
          <div className="links-grid">
            <a href="https://kdp.amazon.com" target="_blank" rel="noopener noreferrer" className="link-card">
              <div className="link-icon">📚</div>
              <h3>Amazon KDP</h3>
              <p>Learn about publishing on Amazon</p>
            </a>
            <div className="link-card" onClick={() => navigate('/register')}>
              <div className="link-icon">🚀</div>
              <h3>Get Started</h3>
              <p>Create your free account</p>
            </div>
            <div className="link-card" onClick={() => navigate('/')}>
              <div className="link-icon">🏠</div>
              <h3>Home</h3>
              <p>Return to homepage</p>
            </div>
          </div>
        </section>

        <section className="contact-section">
          <h2>Still Need Help?</h2>
          <p>Can't find the answer you're looking for? We're here to help!</p>
          <div className="contact-info">
            <p>For additional support, feature requests, or feedback, please email us at:</p>
            <p><a href="mailto:support@tinylittlefox.com" style={{ color: '#4F46E5', fontWeight: 600, fontSize: '18px' }}>support@tinylittlefox.com</a></p>
          </div>
        </section>
      </div>
    </div>
  );
};

export default Help;
